import React from "react";
import PropTypes from "prop-types";
import { Chart, ChartTooltip } from "@progress/kendo-react-charts";
import "hammerjs";
import "./ChartComponent.scss";

// elements
import ChartTitleComponent from "./elements/ChartTitleComponent";
import ChartLegendComponent from "./elements/ChartLegendComponent";
import ChartCategoryAxisComponent from "./elements/ChartCategoryAxisComponent";
import ChartSeriesComponent from "./elements/ChartSeriesComponent";
import ChartValueAxisComponent from "./elements/ChartValueAxisComponent";

/**
 * Chart component
 * @class
 * @property {string} className - Chart - sets additional CSS classes
 * @property {boolean} pannable - Chart pannable
 * @property {boolean} zoomable - Chart zoomable
 * @property {object} style - Chart style
 * @property {object} categoryAxisItemProps - Props of ChartCategoryAxisComponent
 * @property {array} valueAxisItemProps - Props of ChartValueAxisComponent
 * @property {object} legendProps - Props of ChartLegendComponent
 * @property {array} seriesItemProps - Props of ChartSeriesComponent
 * @property {object} titleProps - Props of ChartTitleComponent
 */

const ChartComponent = (props) => {
  const {
    className,
    pannable,
    zoomable,
    style,
    titleProps,
    legendProps,
    valueAxisItemProps,
    categoryAxisItemProps,
    seriesItemProps,
    renderTooltip,
  } = props;

  const renderTooltipFunc = ({ point }) => <span>{point.value}</span>;

  return (
    <div style={{ overflowX: "auto" }}>
      <Chart
        className={className}
        pannable={pannable}
        zoomable={zoomable}
        style={style}
      >
        <ChartLegendComponent legendProps={legendProps} />
        <ChartSeriesComponent seriesItemProps={seriesItemProps} />

        {titleProps && <ChartTitleComponent titleProps={titleProps} />}
        {valueAxisItemProps && (
          <ChartValueAxisComponent valueAxisItemProps={valueAxisItemProps} />
        )}
        {categoryAxisItemProps && (
          <ChartCategoryAxisComponent
            categoryAxisItemProps={categoryAxisItemProps}
          />
        )}
        <ChartTooltip render={renderTooltip || renderTooltipFunc} />
      </Chart>
    </div>
  );
};

ChartComponent.propTypes = {
  className: PropTypes.string,
  pannable: PropTypes.bool,
  zoomable: PropTypes.bool,
  style: PropTypes.object,
  categoryAxisItemProps: PropTypes.shape({
    categories: PropTypes.array,
    title: PropTypes.object,
    axisCrossingValues: PropTypes.array,
    maxDivisions: PropTypes.number,
  }),
  valueAxisItemProps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.shape({
        text: PropTypes.string,
      }),
      name: PropTypes.string,
    })
  ),
  legendProps: PropTypes.shape({
    position: PropTypes.string,
  }).isRequired,
  seriesItemProps: PropTypes.arrayOf(
    PropTypes.shape({
      data: PropTypes.array,
      gap: PropTypes.number,
      name: PropTypes.string,
      spacing: PropTypes.number,
      type: PropTypes.string,
      axis: PropTypes.string,
    })
  ).isRequired,
  titleProps: PropTypes.shape({
    text: PropTypes.string,
  }),
  renderTooltip: PropTypes.func,
};

export default ChartComponent;
